import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { __PERSON_API_URL__, __TOPONYMY_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';
import { HttpResponseList } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesCirculationAreas } from '..';
import { useFormik } from 'formik';
import { FiltersFormCirculationArea, FitersCadastralVM, CirculationAreaCadastral } from './types';
import { transformDataset, transformerListCadastral } from './utils';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CirculationArea } from 'shell/models/circulation-area';
import { CommonToponymyCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import {
  authsObj,
  headCells,
  headCellsCadastral,
  validationSchemaToponymyFilter,
} from './constants';
import { FlagCirculationArea } from 'system/constants/circulation-area-flag';
import { Typography, useEventCallback } from '@mui/material';
import FiltersListToponymy from 'system/components/toponymy/filters-list';
import { CadastralData } from 'shell/models/person';
import { useHttpFetch, useHttpFetch as useHttpFetchNew } from 'shell/hooks/use-http-fetch';
import { noop } from 'shell/utils/noop';
import ListHeader from 'shell/components/shared/list-header';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { CirculationAreaClient } from 'shell/view-models/circulation-area-client';

const CirculationAreasPage = (): React.ReactElement => {
  // stati apertura accordions filters
  const [openCirculationArea, setOpenCirculationArea] = useState<boolean>(true);
  const [openCadastral, setOpenCadastral] = useState<boolean>(true);

  const [isCadastralSearch, setIsCadastralSearch] = useState<boolean>(false);

  const [tableData, setTableData] = useState({
    rowsPerPage: 10,
    page: 0,
  });

  const [deleteAlertModal, setDeleteAlertModal] = useState<
    AlertModal<CirculationAreaClient | CirculationAreaCadastral>
  >({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<
    AlertModal<CirculationAreaClient | CirculationAreaCadastral>
  >({
    element: null,
    open: false,
  });

  const deleteCirculationArea = useHttpClient(`${__TOPONYMY_API_URL__}/areaCircolazione`, {
    options: {
      errorCatching: true,
      method: 'DELETE',
      loading: true,
    },
  });

  const filterTable = useFilterTable<FiltersFormCirculationArea & FitersCadastralVM>();

  const [circulationAreasData, getCirculationAreas] = useHttpFetch<
    HttpResponseList<CirculationArea>,
    HttpResponseList<CirculationArea>
  >(`${__TOPONYMY_API_URL__}/areaCircolazione`, {
    onBootstrap: false,
    options: {
      method: 'GET',
      errorCatching: true,
      loading: true,
    },
    transformer: (response) => response.data,
  });

  const onSubmit = useCallback(
    (formValues: FiltersFormCirculationArea) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterCirculationAreaForm.setFieldValue('page', 0);
      (
        getCirculationAreas({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<CirculationArea>, HttpResponseList<CirculationArea>>
      ).then(() => {
        filterTable.setValues({
          code: formValues?.code || '',
          name: formValues?.name || '',
          number: formValues?.number || '',
          page: 0,
          rowsPerPage: 10,
          orders: {},
        });
      });

      filterTable.setValues({
        ...formValues,
        isCadastral: false,
        sheet: '',
        particle: '',
        subordinate: '',
        hasVisitedChild: false,
        circulationAreaCadastral: null,
      });

      // chiusura accordions filters
      setOpenCirculationArea(false);
      setOpenCadastral(false);
      setIsCadastralSearch(false);
    },
    [getCirculationAreas]
  );

  const data = circulationAreasData.data as HttpResponseList<CirculationArea>;

  const circulationAreas: Array<CirculationAreaClient> = useMemo(() => {
    if (circulationAreasData.isLoading || !circulationAreasData.data) {
      return [];
    }

    return data.payload.list.map((circulationArea) => ({
      description: circulationArea.descrizionePostale || '',
      code: circulationArea.key?.codice,
      hidden: {
        read: false,
        edit: false,
        delete: circulationArea.flag === FlagCirculationArea.End,
      },
    }));
  }, [circulationAreasData, data]);

  const filterCirculationAreaForm = useFormik({
    initialValues: initialValuesCirculationAreas,
    validationSchema: validationSchemaToponymyFilter,
    onSubmit,
  });

  useEffect(() => {
    if (!filterTable.values?.isCadastral && filterTable.values?.hasVisitedChild) {
      filterCirculationAreaForm.setValues(filterTable.values || initialValuesCirculationAreas);
      const queryString = transformDataset(filterTable.values || initialValuesCirculationAreas);
      getCirculationAreas({
        params: {},
        paramsSerializer: () => queryString,
      });
      setOpenCirculationArea(false);
      setOpenCadastral(false);
      setIsCadastralSearch(false);
    } else if (filterTable.values?.isCadastral && filterTable.values?.hasVisitedChild) {
      getListCadastral({
        data: {
          foglio: filterTable.values?.sheet || undefined,
          particella: filterTable.values?.particle || undefined,
          subalterno: filterTable.values?.subordinate || undefined,
        },
      });
      setIsCadastralSearch(true);
      setOpenCirculationArea(false);
      setOpenCadastral(false);
    }
  }, []);

  const count: number = useMemo(() => {
    if (
      !!circulationAreasData.isLoading ||
      !circulationAreasData.data ||
      !data?.payload.totalRows
    ) {
      return 0;
    }
    return data.payload.totalRows;
  }, [data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityCirculationAreaPress = useCallback(
    (cell: CirculationAreaClient): Promise<CirculationAreaClient> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true });
        navigate(
          generatePath('/sistema/toponomastica/area-di-circolazione/dettaglio/:id', {
            id: cell.code?.toString() || '',
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditCirculationAreaPress = useCallback(
    (cell: CirculationAreaClient): Promise<CirculationAreaClient> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true });

        navigate(
          generatePath('/sistema/toponomastica/area-di-circolazione/modifica/:id', {
            id: cell.code?.toString() || '',
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashCirculationAreaPress = useCallback(
    (cell: CirculationAreaClient): Promise<CirculationAreaClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      });
    },
    [setDeleteAlertModal]
  );

  const onVisibilityCirculationAreaCadastralPress = useCallback(
    (cell: CirculationAreaCadastral): Promise<CirculationAreaCadastral> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true, circulationAreaCadastral: cell });

        navigate({
          pathname: generatePath('/sistema/toponomastica/area-di-circolazione/dettaglio/:id', {
            id: cell.code?.toString() || '',
          }),
          search: `?${createSearchParams({
            civico: cell.civic || '',
            esponente: cell.exponent || '',
          })}`,
        });
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditCirculationAreaCadastralPress = useCallback(
    (cell: CirculationAreaCadastral): Promise<CirculationAreaCadastral> => {
      return new Promise((resolve) => {
        filterTable.setValues({ hasVisitedChild: true, circulationAreaCadastral: cell });

        navigate({
          pathname: generatePath('/sistema/toponomastica/area-di-circolazione/modifica/:id', {
            id: cell.code?.toString() || '',
          }),
          search: `?${createSearchParams({
            civico: cell.civic || '',
            esponente: cell.exponent || '',
          })}`,
        });
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashCirculationAreaCadastralPress = useCallback(
    (cell: CirculationAreaCadastral): Promise<CirculationAreaCadastral> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      });
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterCirculationAreaForm.setFieldValue('page', newPage);
    },
    [filterCirculationAreaForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterCirculationAreaForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      filterCirculationAreaForm.setValues(newDataset);
    },
    [filterCirculationAreaForm.setFieldValue, filterCirculationAreaForm.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterCirculationAreaForm.values, orders };
      const queryString = transformDataset(newDataset);
      return (
        getCirculationAreas({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<CirculationArea>, HttpResponseList<CirculationArea>>
      ).then(() => {
        filterCirculationAreaForm.setFieldValue('orders', orders);
      });
    },
    [getCirculationAreas, filterCirculationAreaForm.setFieldValue, filterCirculationAreaForm.values]
  );

  const onCloseDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onYesDeleteAlertModal = useCallback(() => {
    (
      deleteCirculationArea({ url: `/${deleteAlertModal.element?.code}` }) as ResponsePromise<void>
    ).then(() => {
      setDeleteAlertModal({ open: false, element: null });
      setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
    });
  }, [setDeleteAlertModal, deleteCirculationArea, setDeleteSuccessModal, deleteAlertModal.element]);

  const onNoDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onCloseDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset({ ...filterCirculationAreaForm.values, page: 0 });
    (
      getCirculationAreas({
        params: {},
        paramsSerializer: () => queryString,
      }) as ResponsePromise<HttpResponseList<CirculationArea>, HttpResponseList<CirculationArea>>
    ).then(() => setDeleteSuccessModal({ open: false, element: null }));
  }, [getCirculationAreas, setDeleteSuccessModal, filterCirculationAreaForm.values]);

  const onOkDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset({ ...filterCirculationAreaForm.values, page: 0 });
    (
      getCirculationAreas({
        params: {},
        paramsSerializer: () => queryString,
      }) as ResponsePromise<HttpResponseList<CirculationArea>, HttpResponseList<CirculationArea>>
    ).then(() => setDeleteSuccessModal({ open: false, element: null }));
  }, [getCirculationAreas, setDeleteSuccessModal, filterCirculationAreaForm.values]);

  const [listCadastral, getListCadastral] = useHttpFetchNew<
    HttpResponseList<CadastralData>,
    Array<CirculationAreaCadastral>
  >(`${__PERSON_API_URL__}/persona/dati-catastali/terna-catastale`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: transformerListCadastral,
  });

  const listCadastralData: Array<CirculationAreaCadastral> =
    (listCadastral.data as Array<CirculationAreaCadastral>) || [];

  const handleSubmitFilterCadastral = useEventCallback((values: FitersCadastralVM) => {
    getListCadastral({
      data: {
        foglio: values.sheet || undefined,
        particella: values.particle || undefined,
        subalterno: values.subordinate || undefined,
      },
    });

    filterTable.setValues({
      ...initialValuesCirculationAreas,
      ...values,
      isCadastral: true,
      hasVisitedChild: false,
    });

    // chiusura accordions filters
    setOpenCirculationArea(false);
    setOpenCadastral(false);
    setIsCadastralSearch(true);
  });

  return (
    <Fragment>
      <Breadcrumbs items={CommonToponymyCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuova area di circolazione"
        addBtnLabel="Aggiungi nuova area di circolazione"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/toponomastica/area-di-circolazione/nuovo');
        }}
        title="Gestione Aree di Circolazione"
      />
      <div className="pmi-my-3">
        <FiltersListToponymy
          filterCirculationAreaForm={filterCirculationAreaForm}
          setOpenCirculationArea={setOpenCirculationArea}
          openCirculationArea={openCirculationArea}
          setOpenCadastral={setOpenCadastral}
          openCadastral={openCadastral}
          handleSubmitFilterCadastral={handleSubmitFilterCadastral}
        />
      </div>
      {data?.payload && !isCadastralSearch ? (
        circulationAreas?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterCirculationAreaForm.values.orders || {}}
            activeActions={auths}
            page={filterCirculationAreaForm.values.page || 0}
            rowsPerPage={filterCirculationAreaForm.values.rowsPerPage}
            body={circulationAreas}
            onVisibilityPress={onVisibilityCirculationAreaPress}
            onEditPress={onEditCirculationAreaPress}
            onTrashPress={onTrashCirculationAreaPress}
            count={count || 0}
            onRowsPerPageChange={handleChangeRowsPerPage}
            onPageChange={handleChangePage}
            ariaLabel="Lista Aree di Circolazione"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      {listCadastral?.data && isCadastralSearch ? (
        listCadastralData.length > 0 ? (
          <Table
            noSort
            onSort={noop}
            head={headCellsCadastral}
            orders={{}}
            page={tableData.page}
            rowsPerPage={tableData.rowsPerPage}
            body={listCadastralData}
            onVisibilityPress={onVisibilityCirculationAreaCadastralPress}
            onEditPress={onEditCirculationAreaCadastralPress}
            onTrashPress={onTrashCirculationAreaCadastralPress}
            count={listCadastralData.length || 0}
            onPageChange={(newPage) =>
              setTableData({
                rowsPerPage: tableData.rowsPerPage,
                page: newPage,
              })
            }
            onRowsPerPageChange={(rowsPerPage) =>
              setTableData({
                rowsPerPage,
                page: 0,
              })
            }
            ariaLabel="Lista Terna catastale"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Area"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Area"
        message={`La cancellazione dell'area ${
          (deleteSuccessModal.element as CirculationAreaClient)?.description
        } è avvenuta con successo.`}
        onClose={onCloseDeleteSuccessModal}
        onOk={onOkDeleteSuccessModal}
      />
    </Fragment>
  );
};

CirculationAreasPage.displayName = 'Circulation areas page component';

export default CirculationAreasPage;
